import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faUserMd, 
  faUserTie,
  faRobot
} from '@fortawesome/free-solid-svg-icons';

/**
 * Chat Message Component
 * Displays a single chat message with appropriate styling based on sender role
 */
export const ChatMessage = ({ message, role, characterName }) => {
    // Determine if this is an AI message
    const isAi = role === 'therapist' || (role !== 'writer' && role !== 'character');
    
    // Get styling based on role
    const getRoleStyles = () => {
        switch (role) {
            case 'writer':
                return 'bg-primary text-white'; // Writer is blue
            case 'therapist':
                return 'bg-success text-white'; // Therapist is green
            case 'character':
                return 'bg-warning'; // Character is yellow/orange
            default:
                return 'bg-secondary text-white'; // Default is gray
        }
    };

    // Get display name based on role
    const getDisplayName = () => {
        if (role === 'character' && characterName) {
            return characterName;
        }
        return role.charAt(0).toUpperCase() + role.slice(1);
    };

    // Format text with basic markdown-like syntax
    const formatText = (text) => {
        if (typeof text !== 'string') return '';
        if (!text) return '';
        
        return text
            .replace(/### (.*?)(\n|$)/g, '<h3 class="mt-4 mb-2 fw-bold">$1</h3>')
            .replace(/#### (.*?)(\n|$)/g, '<h4 class="mt-3 mb-2 fw-bold">$1</h4>')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/- (.*?)(\n|$)/g, '<li class="ms-4">$1</li>')
            .replace(/(<li.*?<\/li>\n?)+/g, '<ul class="list-unstyled mb-3">$&</ul>');
    };

    return (
        <div className={`d-flex mb-3 ${isAi ? 'justify-content-end' : 'justify-content-start'}`}>
            {!isAi && (
                <div className="me-2">
                    <div className="rounded-circle bg-secondary p-2 text-white">
                        <FontAwesomeIcon icon={role === 'character' ? faUserTie : faUser} />
                    </div>
                </div>
            )}
            
            <div 
                className={`chat-message rounded p-3 ${getRoleStyles()}`}
                style={{ maxWidth: '75%' }}
            >
                <div className="small text-uppercase mb-1 opacity-75">
                    {getDisplayName()}
                </div>
                <div dangerouslySetInnerHTML={{
                    __html: formatText(message)
                }} />
            </div>
            
            {isAi && (
                <div className="ms-2">
                    <div className="rounded-circle bg-primary p-2 text-white">
                        <FontAwesomeIcon icon={role === 'therapist' ? faUserMd : faRobot} />
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Topic Card Component
 * Displays a therapy topic with expandable details
 */
export const TopicCard = ({ topic, onSelectQuestion }) => {
    const [expanded, setExpanded] = React.useState(false);
    
    // Get background color class based on importance
    const getImportanceClass = () => {
        switch (topic.importance?.toLowerCase()) {
            case 'high':
                return 'border-danger';
            case 'medium':
                return 'border-warning';
            case 'low':
                return 'border-info';
            default:
                return 'border-secondary';
        }
    };
    
    // Get badge color based on importance
    const getImportanceBadgeClass = () => {
        switch (topic.importance?.toLowerCase()) {
            case 'high':
                return 'bg-danger';
            case 'medium':
                return 'bg-warning text-dark';
            case 'low':
                return 'bg-info text-dark';
            default:
                return 'bg-secondary';
        }
    };
    
    return (
        <div className={`card mb-3 ${getImportanceClass()}`}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                <h5 className="mb-0 fs-6">{topic.category}</h5>
                <div>
                    <span className={`badge me-2 ${getImportanceBadgeClass()}`}>{topic.importance}</span>
                    <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? 'Less' : 'More'}
                    </button>
                </div>
            </div>
            
            {expanded && (
                <div className="card-body">
                    <p className="card-text">{topic.explanation}</p>
                    
                    {topic.related_topics && topic.related_topics.length > 0 && (
                        <div className="mb-3">
                            <strong>Related Topics:</strong>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                                {topic.related_topics.map((relatedTopic, index) => (
                                    <span key={index} className="badge bg-light text-dark border">
                                        {relatedTopic}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {topic.source_references && topic.source_references.length > 0 && (
                        <div className="mb-3">
                            <strong>Source References:</strong>
                            <ul className="list-group list-group-flush small">
                                {topic.source_references.map((ref, index) => (
                                    <li key={index} className="list-group-item ps-0 border-0 py-1">
                                        {ref}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {topic.impact_on_narrative && (
                        <div className="mb-3">
                            <strong>Impact on Narrative:</strong>
                            <p className="card-text small mt-1">{topic.impact_on_narrative}</p>
                        </div>
                    )}
                    
                    {topic.potential_questions && topic.potential_questions.length > 0 && (
                        <div>
                            <strong>Potential Questions:</strong>
                            <div className="list-group mt-2">
                                {topic.potential_questions.map((question, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="list-group-item list-group-item-action text-start small"
                                        onClick={() => onSelectQuestion(question)}
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};